import {
  getAppliedCouponsOnViewCartPage,
  Item,
  setCarouselInteraction,
  updateUserWithNewCartTotal,
} from "~/userAnalytics";
import { sendGtmEvent, clearEcommerceInDataLayer } from "../definitions";
import {
  sendFacebookPixelAddToCartEvents,
  sendPinterestAddToCartEvents,
} from "./addToCartApiHelpers";
import { EcommerceData, formatEcommerceData } from "./addToCartHelpers";
import { sendCustomerIoUpdateCartEvent } from "../CustomerIo/CustomerIoEvents";
import {
  createProducts,
  formatProductsForCustomerIo,
  formatDefaultProducts,
  AnalyticsProduct,
} from "../productHelpers";

export type AtcAnalyticsProps = {
  source: string;
  carouselProps?: {
    carouselName: string;
    carouselModel: string;
    position: number;
    carouselPlacementId?: string;
    shouldSetInteraction?: boolean;
  };
  customerIoRunOptions?: "skip" | "only";
};

const sendCustomerIoAddToCartEvents = (
  baseProductsData: AnalyticsProduct[],
  coupons: string,
) => {
  const customerIoProducts = formatProductsForCustomerIo(baseProductsData, [
    "quantity",
    "reviews",
    "brand",
    "imageUrl",
    "overallRating",
  ]);
  sendCustomerIoUpdateCartEvent("Add to Cart", customerIoProducts, coupons);
};

export const addToCartAnalytics = async (
  atcProps: AtcAnalyticsProps,
  ...items: Item[]
) => {
  let total = 0;
  const feedIdentifiers: string[] = [];

  if (atcProps?.carouselProps?.shouldSetInteraction) {
    items.forEach((item) => {
      setCarouselInteraction(
        item.itemNumber,
        atcProps?.carouselProps?.carouselPlacementId ?? "",
      );
    });
  }

  const baseProductsData = await createProducts(items, true);
  const products = formatDefaultProducts(baseProductsData);
  if (products.length === 0) {
    return;
  }

  const coupons = getAppliedCouponsOnViewCartPage();
  if (atcProps.customerIoRunOptions !== "skip") {
    sendCustomerIoAddToCartEvents(baseProductsData, coupons);
    if (atcProps.customerIoRunOptions === "only") {
      return;
    }
  }

  for (const product of products) {
    total += Number(product?.price ?? 0) * Number(product?.quantity ?? 0);
    if (product.feedIdentifier) {
      feedIdentifiers.push(product.feedIdentifier);
    }
  }
  // Done to avoid any floating point errors
  total = parseFloat(total.toFixed(2));
  // Note that prices here are only using P1, as the actual price can vary depending on user to user, so may vary slightly from the actual value.
  const newCartTotal = await updateUserWithNewCartTotal(true, total);
  if (newCartTotal.isError) {
    return;
  }
  sendPinterestAddToCartEvents(products);
  sendFacebookPixelAddToCartEvents(feedIdentifiers, total.toString());
  const formattedEcommerceProducts = formatEcommerceData(products, {
    total,
    position: atcProps.carouselProps?.position ?? 0,
  });

  sendGtmAddToCartEvents(
    formattedEcommerceProducts,
    newCartTotal.value,
    atcProps,
  );
};

const sendCustomerIoRemoveFromCartEvents = (
  baseProductsData: AnalyticsProduct[],
  coupons: string,
  customerIoRunOptions: "empty" | "remove" | "only" = "remove",
) => {
  const customerIoProducts = formatProductsForCustomerIo(baseProductsData, [
    "quantity",
    "brand",
    "reviews",
    "imageUrl",
    "overallRating",
  ]);
  if (customerIoRunOptions === "empty") {
    sendCustomerIoUpdateCartEvent("Empty Cart", [], coupons);
  } else {
    customerIoProducts.forEach((customerIoProduct) => {
      sendCustomerIoUpdateCartEvent(
        "Remove from Cart",
        [customerIoProduct],
        coupons,
      );
    });
  }
};

export const removeFromCartAnalytics = async (
  items: Item[],
  customerIoRunOptions: "empty" | "remove" | "only" = "remove",
) => {
  const baseProductsData = await createProducts(items, true);
  if (baseProductsData.length === 0) {
    return;
  }

  const coupons = getAppliedCouponsOnViewCartPage();
  sendCustomerIoRemoveFromCartEvents(
    baseProductsData,
    coupons,
    customerIoRunOptions,
  );
  if (customerIoRunOptions === "only") {
    return;
  }
  const itemTotalCosts = items.map(
    (item) => (item?.price ?? 0) * (item?.quantity ?? 0),
  );
  const totalCost = itemTotalCosts.reduce((acc, curr) => acc + curr, 0);
  const newCartTotal = await updateUserWithNewCartTotal(false, totalCost);
  if (newCartTotal.isError) {
    return;
  }

  const products = formatDefaultProducts(baseProductsData);
  const formattedEcommerceProduct = formatEcommerceData(products, {
    total: totalCost,
  });

  clearEcommerceInDataLayer();
  sendGtmEvent({
    event: "productRemoveFromCart",
    cart_total: newCartTotal.value,
    ecommerce: formattedEcommerceProduct,
  });
};

const sendGtmAddToCartEvents = (
  ecommerce: EcommerceData,
  cartTotal: number,
  atcProps: AtcAnalyticsProps,
) => {
  clearEcommerceInDataLayer();
  sendGtmEvent({
    event: "addToCart",
    atc_source: atcProps.carouselProps?.carouselName
      ? "carousel"
      : atcProps.source,
    cart_total: cartTotal,
    ecommerce: { ...ecommerce },
    // Reset carousel props to empty string if not present otherwise variables will be previous values
    carouselName: atcProps.carouselProps?.carouselName ?? "",
    carouselModel: atcProps.carouselProps?.carouselModel ?? "",
  });
};
